/* eslint-disable no-prototype-builtins */
/* eslint-disable @typescript-eslint/no-explicit-any */

import { DataTable } from "components/Datatable";
import { IAccountConciliation } from "src/@types/deposit-conference";

import { Paginated } from "@/@types/paginated";
import { useConfirmationAlert } from "@/hook/confirmation-alert/hooks/actions";
import { useDialogModal } from "@/hook/handle-modal/hooks/actions";
import {
  useGetNsuQuery,
  useUpdateBatchNsuMutation,
} from "@/services/conciliation/nsu";
import { transformObjectToArrayByKey } from "@/utils/formData";
import { useState } from "react";
import { useForm } from "react-hook-form";

import { cn, formatNsuList } from "@/lib/utils";
import { NsuStatusEnum } from "@/enum/nsu-status";
import { ReceiptRequestErrorCode } from "@/enum/receipt-request-error-code";
import { IRequestNsuReceiptResponse } from "@/@types/email";
import {
  useSendEmailRequestNsuEvidenceMutation,
  useSendEmailRequestNsuReceiptMutation,
} from "@/services/email";
import {
  useGetOneStoreConciliationQuery,
  useReprocessNsuMutation,
} from "@/services/store-conciliation";
import { LoadingDots } from "components/loadingDots";
import { Button } from "components/ui/button";
import { toast } from "components/ui/use-toast";
import { RotateCwSquare } from "lucide-react";
import { IoRefresh } from "react-icons/io5";
import { MdEmail } from "react-icons/md";
import { MESSAGES } from "../../utils/messages";
import { MESSAGES_NSU } from "../utils/messages";
import { useColumns } from "./columns";
import { NsuRejectedWarning } from "./components/nsu-rejected-warning";

interface DepositConferenceProps {
  isClosed: boolean;
  conciliation_id: string;
}

interface INsuFormData {
  [key: string]: any;
}

export const DepositConference: React.FC<DepositConferenceProps> = ({
  isClosed,
  conciliation_id,
}) => {
  const [updateBatchNsu, { isLoading }] = useUpdateBatchNsuMutation();
  const [isReprocessing, setIsReprocessing] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const { handleAlert } = useConfirmationAlert();

  const { data: conciliation } = useGetOneStoreConciliationQuery({
    idTransaction: conciliation_id as string,
  });

  const {
    data: nsuData,
    refetch,
    isFetching,
    isLoading: isNsuLoading,
  } = useGetNsuQuery({
    perPage: 100,
    page: 1,
    conciliation_id: conciliation_id,
  });

  const { data } = nsuData ?? ({} as Paginated<IAccountConciliation>);
  const [reprocessNsu] = useReprocessNsuMutation();

  const [sendEmailRequestNsuEvidence, { isLoading: requestNsuEmailLoading }] =
    useSendEmailRequestNsuEvidenceMutation();

  const [
    sendEmailRequestNsuReceipt,
    { isLoading: requestNsuReceiptEmailLoading },
  ] = useSendEmailRequestNsuReceiptMutation();

  const { handleModal } = useDialogModal();

  const [requestedNsuIds, setRequestedNsuIds] = useState<string[]>([]);
  const [requestedReceipts, setRequestedReceipts] = useState<boolean>(false);

  const methods = useForm();
  const {
    handleSubmit: handleFormSubmit,
    formState: { errors },
  } = methods;

  const isNsuCodeEmpty = (nsuNumber: any): boolean => {
    return (
      nsuNumber === null ||
      nsuNumber === undefined ||
      nsuNumber === "" ||
      nsuNumber === " "
    );
  };

  const hasEmptyNsuCode = (): boolean => {
    return (
      data?.some((nsu: IAccountConciliation) => isNsuCodeEmpty(nsu?.number)) ??
      false
    );
  };

  const columns = useColumns({
    methods,
    requestedNsuIds,
    requestedReceipts,
  });

  // Filtrar NSUs rejeitados para o warning
  const rejectedNsus = (data ?? [])
    .filter(
      (nsu: IAccountConciliation) => nsu.status === NsuStatusEnum.REJECTED,
    )
    .map((nsu: IAccountConciliation) => ({
      id: Number(nsu.id),
      number: String(nsu.number ?? ""),
      amount: nsu.amount,
    }));

  const doReprocess = async () => {
    setIsReprocessing(true);
    try {
      await reprocessNsu(conciliation_id).unwrap();
    } catch (error) {
      console.error("Erro ao reprocessar:", error);
    } finally {
      setIsReprocessing(false);
    }
  };

  const handleReprocess = async () => {
    const hasNullReprocessKey = (data ?? []).some(
      (nsu: IAccountConciliation) =>
        nsu.reprocess_key === null ||
        nsu.reprocess_key === undefined ||
        nsu.reprocess_key === "" ||
        nsu.reprocess_key === " ",
    );

    if (hasNullReprocessKey) {
      handleAlert({
        isOpen: true,
        isLoading: false,
        title: "Atenção!",
        description:
          "Reprocessar as NSUs desta loja apagará todos os anexos e respostas já vinculados. Deseja realmente continuar?",
        confirmText: "Sim",
        onConfirm: async () => {
          await doReprocess();
        },
      });
      return;
    }

    await doReprocess();
  };

  const handleSave = async (formData: INsuFormData) => {
    try {
      const nsuArray = transformObjectToArrayByKey(formData, "nsu");

      if (nsuArray.length === 0) {
        toast(MESSAGES.NO_DATA_CHANGED.ERROR);
        return;
      }

      // Verificar se há NSUs com adjustment_amount > 0 sem justificativa
      const nsusWithoutJustification = nsuArray.filter((nsu: any) => {
        const adjustmentAmount = parseFloat(nsu.value.adjustment_amount) || 0;
        const hasJustification =
          nsu.value.justification_id && nsu.value.justification_id !== "";
        return adjustmentAmount > 0 && !hasJustification;
      });

      if (nsusWithoutJustification.length > 0) {
        toast({
          variant: "destructive",
          title: "Justificativa obrigatória",
          description:
            "NSUs com valor de extrato preenchido devem ter uma justificativa selecionada.",
          duration: 5000,
        });
        return;
      }

      const formattedNsuArray = nsuArray.map((nsu: any) => ({
        id: nsu.value.id,
        version: nsu.value.version,
        validated: nsu.value.conferred ?? false,
        is_loss: nsu.value.is_loss ?? false,
        is_payroll_discount: nsu.value.is_payroll_discount ?? false,
        adjustment_amount: parseFloat(nsu.value.adjustment_amount) || 0,
        justification_id: nsu.value.justification_id
          ? parseInt(nsu.value.justification_id)
          : undefined,
      }));

      await updateBatchNsu(formattedNsuArray).unwrap();
      toast(MESSAGES.UPDATE.SUCCESS);
    } catch (e) {
      toast(MESSAGES.UPDATE.ERROR);
    }
  };

  async function handeNSURequest() {
    if (
      !conciliation?.store?.code ||
      !conciliation_id ||
      requestNsuEmailLoading ||
      !hasEmptyNsuCode()
    )
      return;

    try {
      await sendEmailRequestNsuEvidence({
        conciliation_id,
        store_code: conciliation?.store?.code,
      }).unwrap();

      if (data) {
        const emptyNsuIds = data
          .filter((nsu: IAccountConciliation) => isNsuCodeEmpty(nsu?.number))
          .map((nsu: IAccountConciliation) => nsu.id?.toString())
          .filter(Boolean) as string[];

        setRequestedNsuIds((prev) => [...prev, ...emptyNsuIds]);
      }

      toast(MESSAGES_NSU.REQUEST_EMAIL.SUCCESS);
    } catch (error: any) {
      const isStoreNotAllowed =
        error?.status === 400 &&
        error?.data?.messages?.includes("Store not allowed to receive emails");

      toast({
        variant: "destructive",
        description: isStoreNotAllowed
          ? "Esta loja não tem permissão para receber emails"
          : "Erro ao solicitar evidência",
        duration: 5000,
      });
    }
  }

  const allNsusAlreadyRequested =
    (data ?? []).length > 0 &&
    (data ?? []).every(
      (nsu: IAccountConciliation) => nsu.receipt_requested === true,
    );

  const buildSuccessToastMessage = (
    result: IRequestNsuReceiptResponse,
  ): string => {
    const requested = formatNsuList(result.requested_nsu_numbers);
    if (result.total_skipped === 0) {
      return `Solicitações enviadas para o(s) NSU(s): ${requested}.`;
    }
    const skipped = formatNsuList(result.skipped_nsu_numbers);
    return `Solicitações enviadas para o(s) NSU(s): ${requested}. Já solicitados anteriormente: ${skipped}.`;
  };

  const isReceiptAlreadyRequestedError = (error: any): boolean =>
    error?.status === 409 &&
    error?.data?.code === ReceiptRequestErrorCode.RECEIPT_ALREADY_REQUESTED;

  const isReceiptLockRaceError = (error: any): boolean =>
    error?.status === 409 &&
    error?.data?.code === ReceiptRequestErrorCode.RECEIPT_LOCK_RACE;

  async function handeNSUReceiptRequest() {
    if (
      !conciliation?.store?.code ||
      !conciliation_id ||
      requestNsuReceiptEmailLoading ||
      allNsusAlreadyRequested
    )
      return;
    try {
      const result = await sendEmailRequestNsuReceipt({
        conciliation_id,
        store_code: conciliation?.store?.code,
      }).unwrap();

      setRequestedReceipts(true);

      const message = buildSuccessToastMessage(result);
      if (result.total_skipped === 0) {
        toast({ description: message });
      } else {
        toast({ description: message, duration: 8000 });
      }
    } catch (error: any) {
      if (
        isReceiptAlreadyRequestedError(error) ||
        isReceiptLockRaceError(error)
      ) {
        toast({
          variant: "destructive",
          description:
            error?.data?.message ?? "Comprovantes já solicitados.",
          duration: 8000,
        });
        await refetch();
        return;
      }

      const isStoreNotAllowed =
        error?.status === 400 &&
        error?.data?.messages?.includes("Store not allowed to receive emails");

      toast({
        variant: "destructive",
        description: isStoreNotAllowed
          ? "Esta loja não tem permissão para receber emails"
          : "Erro ao solicitar evidência",
        duration: 5000,
      });
    }
  }

  const handleRefetch = async () => {
    setIsRefetching(true);
    try {
      await refetch();
    } finally {
      setIsRefetching(false);
    }
  };

  return (
    // O TableCell do Datatable aplica text-left, o que deixava o valor na esquerda
    // enquanto o titulo da coluna fica centralizado. Centraliza as celulas apenas
    // desta tabela, sem alterar o componente compartilhado.
    <div className="[&_td]:text-center [&_td>div]:justify-center">
      <NsuRejectedWarning rejectedNsus={rejectedNsus} />
      <DataTable
        data={data}
        columns={columns}
        emptyMessage="Selecione uma data de conciliação para ver os dados."
        isLoading={isNsuLoading}
      />
      <div className="flex items-center justify-between gap-3 mt-4 p-3 bg-[#F9FBFC] rounded-lg border">
        <Button
          variant="outline"
          onClick={() =>
            handleModal({
              isOpen: false,
            })
          }
        >
          Voltar
        </Button>
        <div className="flex gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={handleRefetch}
            className="hover:text-white text-[#4B303C]"
            disabled={isRefetching}
          >
            <IoRefresh
              className={cn(
                "w-4 h-4 transition-transform duration-500",
                isRefetching && "animate-spin",
              )}
            />
          </Button>
          <Button
            variant="outline"
            className={cn(
              "hover:text-white text-[#4B303C]",
              isReprocessing && "bg-[#4B303C] text-white hover:text-white",
            )}
            onClick={handleReprocess}
            disabled={isClosed || isReprocessing}
          >
            {!isReprocessing && <RotateCwSquare />}
            <LoadingDots
              isLoading={isReprocessing}
              darkMode={true}
              text={"Reprocessar NSUs"}
            />
          </Button>
          <Button
            onClick={async () => await handeNSUReceiptRequest()}
            variant="outline"
            className={`${
              requestNsuReceiptEmailLoading
                ? "bg-[#4B303C] text-white hover:text-white"
                : "hover:text-white text-[#4B303C]"
            } `}
            disabled={
              isClosed ||
              allNsusAlreadyRequested ||
              requestNsuReceiptEmailLoading
            }
          >
            <MdEmail />
            <LoadingDots
              isLoading={requestNsuReceiptEmailLoading ?? false}
              text={"Solicitar Comprovantes"}
            />
          </Button>
          <Button
            onClick={async () => await handeNSURequest()}
            disabled={isClosed || !hasEmptyNsuCode()}
            variant="outline"
            className={`${
              requestNsuEmailLoading
                ? "bg-[#4B303C] text-white hover:text-white"
                : "hover:text-white text-[#4B303C]"
            } `}
          >
            <MdEmail />
            <LoadingDots
              isLoading={requestNsuEmailLoading ?? false}
              text={"Solicitar NSU"}
            />
          </Button>
          <Button
            className="rounded-md"
            disabled={
              JSON.stringify(errors ?? {}) !== "{}" ||
              isLoading ||
              isFetching ||
              isNsuLoading ||
              isClosed === true
            }
            onClick={async () => await handleFormSubmit(handleSave)()}
          >
            <LoadingDots
              isLoading={isLoading || isFetching || isNsuLoading}
              text="Salvar"
            />
          </Button>
        </div>
      </div>
    </div>
  );
};
